import axios from "axios";

/* ===============================
   DJANGO CLIENT
================================= */
const token = localStorage.getItem("token");

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_DJANGO_API_URL,
  headers: {
    "Content-Type": "application/json",
     ...(token && { Authorization: `Bearer ${token}` }),
    // "Authorization": `Bearer ${localStorage.getItem("token")}`,
  },
});

const PUBLIC_PATHS = [
  "/login",
  "/forgot-password",
  "/security-question-login",
  "/reset-password",
  "/visitor-register",
  "/agency-form",
];

const onPublicPage = () => PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));

/* ===============================
   Session end
================================= */
// Most endpoints answer 403 rather than 401 for an expired or time-restricted token, so
// a session could die and the app would just show empty tables forever. Decide from the
// body as well as the status.
const isSessionEnded = (error) => {
  const status = error.response?.status;
  if (status === 401) return true;
  if (status !== 403) return false;
  const d = error.response?.data || {};
  const text = String(d.code || d.error || d.message || d.detail || "").toLowerCase();
  return (
    d.code === "login_time_restricted" ||
    text.includes("token has expired") ||
    text.includes("invalid token") ||
    text.includes("authentication credentials were not provided") ||
    text.includes("user not found")
  );
};

let redirecting = false;

const endSession = (error) => {
  const d = error.response?.data || {};
  // Logged out because the employee is outside their allowed login hours →
  // remember why, so the login page can show it.
  if (d.code === "login_time_restricted") {
    try { sessionStorage.setItem("login_block_reason", d.message || d.error || ""); } catch { /* */ }
  }
  ["token", "refreshToken", "user", "employee", "isFirstLogin", "savePassword"]
    .forEach((k) => { try { localStorage.removeItem(k); } catch { /* */ } });
  try { localStorage.setItem("logout-at", String(Date.now())); } catch { /* */ }

  if (redirecting || onPublicPage()) return;
  redirecting = true;
  // Keep where they were so login can send them back instead of always the dashboard.
  try {
    const here = window.location.pathname + window.location.search;
    if (here && here !== "/") sessionStorage.setItem("postLoginRedirect", here);
  } catch { /* */ }
  window.location.href = "/login";
};

/* ===============================
   Access-token refresh (single flight)
================================= */
let refreshing = null;

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;
  if (!refreshing) {
    // A bare axios call: no interceptors, so a failing refresh can't recurse.
    refreshing = axios
      .post(`${process.env.REACT_APP_DJANGO_API_URL}/refresh`, { refresh_token: refreshToken })
      .then((res) => {
        const data = res.data?.data || res.data || {};
        if (!data.access_token) return null;
        localStorage.setItem("token", data.access_token);
        if (data.refresh_token) localStorage.setItem("refreshToken", data.refresh_token);
        return data.access_token;
      })
      .catch(() => null)
      .finally(() => { refreshing = null; });
  }
  return refreshing;
};

/* ===============================
   Attach Interceptors
================================= */
const attachRequestInterceptor = (client, tokenKey) => {
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(tokenKey); // dynamic token
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // For FormData (file uploads), drop the default JSON Content-Type so the browser
      // sets `multipart/form-data; boundary=…` — otherwise the files never reach Django.
      if (typeof FormData !== "undefined" && config.data instanceof FormData) {
        if (config.headers && typeof config.headers.delete === "function") {
          config.headers.delete("Content-Type");
        } else if (config.headers) {
          delete config.headers["Content-Type"];
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

const attachResponseInterceptor = (client) => {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config || {};
      if (!isSessionEnded(error) || config.__isRetry) return Promise.reject(error);

      // Outside the allowed login hours no new token will help — log out straight away.
      if (error.response?.data?.code !== "login_time_restricted") {
        const fresh = await refreshAccessToken();
        if (fresh) {
          config.__isRetry = true;
          config.headers = { ...(config.headers || {}), Authorization: `Bearer ${fresh}` };
          return client(config);
        }
      }
      endSession(error);
      return Promise.reject(error);
    }
  );
};

attachRequestInterceptor(apiClient, "token");
attachResponseInterceptor(apiClient);

export default apiClient;
